import { Inject, ElementRef, Optional, Directive, Input, OnChanges, SimpleChanges, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MAT_FORM_FIELD, MatFormField }
	from '@angular/material/form-field';
import { MAT_INPUT_VALUE_ACCESSOR } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput } from '@copo/mat-datepicker-new';

import { DateFormats } from '../utils/date.format';

const MAT_DATEPICKER_VALUE_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	// eslint-disable-next-line @angular-eslint/no-forward-ref,@typescript-eslint/no-use-before-define
	useExisting: forwardRef(() => CopoDatepickerInputDirective),
	multi: true
};

const MAT_DATEPICKER_VALIDATORS = {
	provide: NG_VALIDATORS,
	// eslint-disable-next-line @angular-eslint/no-forward-ref,@typescript-eslint/no-use-before-define
	useExisting: forwardRef(() => CopoDatepickerInputDirective),
	multi: true
};

@Directive({
	selector: 'input[copoDatepicker]',
	providers: [
		MAT_DATEPICKER_VALUE_ACCESSOR,
		MAT_DATEPICKER_VALIDATORS,
		{ provide: MAT_INPUT_VALUE_ACCESSOR, useExisting: CopoDatepickerInputDirective },
		{ provide: MAT_DATE_FORMATS, useClass: DateFormats },
	],

	host: {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'[attr.aria-haspopup]': '_datepicker ? "dialog" : null',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'[attr.aria-owns]': '(_datepicker?.opened && _datepicker.id) || null',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'[attr.min]': 'min ? _dateAdapter.toIso8601(min) : null',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'[attr.max]': 'max ? _dateAdapter.toIso8601(max) : null',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'[disabled]': 'disabled',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'(input)': '_onInput($event.target.value)',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'(change)': '_onChange()',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'(blur)': '_onBlur()',
		// eslint-disable-next-line @typescript-eslint/naming-convention
		'(keydown)': '_onKeydown($event)',
	},
	exportAs: 'copoDatepickerInput',
})
export class CopoDatepickerInputDirective<D> extends MatDatepickerInput<D> implements OnChanges {
	@Input() copoFormat: string;

	currentValue: ElementRef<HTMLInputElement>;
	private readonly copoFormats: DateFormats;

	constructor(
		elementRef: ElementRef<HTMLInputElement>,
		@Optional() dateAdapter: DateAdapter<D>,
		@Optional() @Inject(MAT_DATE_FORMATS) copoFormats: MatDateFormats,

		@Optional() @Inject(MAT_FORM_FIELD) matFormField: MatFormField
	) {
		super(elementRef, dateAdapter, copoFormats, matFormField);
		if (!(copoFormats instanceof DateFormats)) {
			throw new TypeError('MatDateFormats should be an instance of CopoDateFormats');
		}
		this.copoFormats = copoFormats;
		this.copoFormat = copoFormats.format;
		this.currentValue = elementRef;
	}

	@Input() set copoDatepicker(value: MatDatepicker<D>) {
		this.matDatepicker = value;
	}

	override ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);
		if (!('copoFormat' in changes) || changes['copoFormat'].currentValue === '') {
			return;
		}
		this.copoFormats.format = this.copoFormat;
		if (this.value) {
			this.writeValue(this.value);
		} else {
			this._onInput(this.currentValue.nativeElement.value);
		}
	}
}
